import React, { useEffect, useState } from "react";
import styles from "./QuizCreator.module.css";
import ReusableIosSwitch from "../../../../../components/switchButton/SwitchButton";
import ReusableTooltip from "../../../../../components/tooltip/ReusableTooltip";
import InfoIcon from "@mui/icons-material/Info";
import { useDispatch, useSelector } from "react-redux";
import {
  createQuiz,
  getCourseDetails,
  getTemplates,
} from "../../../../../redux/actions/courses";
import { updateQuiz } from "../../../../../redux/actions/quiz";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import SubjectIcon from "@mui/icons-material/Subject";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import {
  useHistory,
  useLocation,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import DiscardModal from "./DiscardModa";
import CustomSelect from "./CustomSelect";

const QuizCreator = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const params = useParams();
  const location = useLocation();
  const history = useHistory();

  console.log("location ==> ", location);
  const isEditing = location?.state?.isEditing;
  const editingQuiz = location?.state?.quiz;

  const courseID = params.id;
  const currentCourse = useSelector((state) => state?.courses?.currentCourse);
  const sections = currentCourse?.section;
  console.log("sections ==> ", sections);
  const templates = useSelector((state) => state.courses.selectedTemplate);
  const quizDataFromStore = useSelector((state) => state.courses);

  const [quizData, setQuizData] = useState({
    title: "",
    details: "",
    due_date: new Date().toISOString(),
    is_lock: false,
    assign_all: true,
    is_timed: false,
    duration: 0,
    question_randomized: false,
    reattempt: 1,
    show_answers: true,
    section_id: location?.state?.sectionId,
    isPublished: false,
    template: "",
  });

  const [timeEnabled, setTimeEnabled] = useState(false);
  const [timeLimit, setTimeLimit] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [openDiscardModal, setOpenDiscardModal] = useState(false);

  useEffect(() => {
    if (!currentCourse?.id) {
      dispatch(getCourseDetails(courseID));
    }
  }, [currentCourse?.id]);

  useEffect(() => {
    setQuizData({
      ...quizData,
      section_id: location?.state?.sectionId,
    });
  }, [location?.state]);

  useEffect(() => {
    dispatch(getTemplates());
  }, []);

  useEffect(() => {
  // if routed here to edit, prefill the form
    if (isEditing && editingQuiz) {
      setQuizData((prev) => ({
        ...prev,
        id: editingQuiz.id,
        title: editingQuiz.title ?? "",
        details: editingQuiz.details ?? "",
        due_date: editingQuiz.due_date ?? new Date().toISOString(),
        is_lock: editingQuiz.is_lock ?? false,
        assign_all: editingQuiz.assign_all ?? true,
        is_timed: editingQuiz.is_timed ?? false,
        duration: editingQuiz.duration ?? 0,
        question_randomized: editingQuiz.question_randomized ?? false,
        reattempt: editingQuiz.reattempt ?? 1,
        show_answers: editingQuiz.show_answers ?? true,
        section_id: location?.state?.sectionId ?? editingQuiz.section_id ?? prev.section_id,
        template: editingQuiz.template ? String(editingQuiz.template) : "",
        isPublished: editingQuiz.isPublished ?? false,
      }));

      // if it is timed, set timeEnabled + timeLimit based on duration
      if (editingQuiz.is_timed) {
        setTimeEnabled(true);
        // duration is stored as minutes.decimal (as you had)
        const totalMinutes = Number(editingQuiz.duration) || 0;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.floor(totalMinutes % 60);
        const seconds = Math.round(((totalMinutes - Math.floor(totalMinutes)) * 60));
        setTimeLimit({ hours, minutes, seconds });
      }
    }
  }, [isEditing, editingQuiz]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setQuizData({ ...quizData, [name]: value });
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setQuizData({ ...quizData, [name]: value });
  };

  const handleTimeToggle = () => {
    const newToggle = !timeEnabled;
    setTimeEnabled(newToggle);

    if (!newToggle) {
      setTimeLimit({ hours: 0, minutes: 0, seconds: 0 });
      setQuizData({ ...quizData, is_timed: false, duration: 0 });
    } else {
      setQuizData({ ...quizData, is_timed: true }); // Set is_timed to true when enabled
    }
  };

  const handleTimeChange = (field, value) => {
    let intValue = parseInt(value || 0, 10);

    // Clamp values based on field
    if (field === "hours") {
      intValue = Math.max(0, Math.min(intValue, 99)); // up to 99 hours (customize limit)
    } else if (field === "minutes" || field === "seconds") {
      intValue = Math.max(0, Math.min(intValue, 59));
    }

    const updatedTime = { ...timeLimit, [field]: intValue };
    setTimeLimit(updatedTime);

    // Convert to duration (minutes + seconds fraction)
    const totalMinutes = updatedTime.hours * 60 + updatedTime.minutes;
    const secondsFraction = updatedTime.seconds / 60;
    const durationAsFloat = parseFloat(
      (totalMinutes + secondsFraction).toFixed(2)
    );

    setQuizData((prev) => ({
      ...prev,
      is_timed: true,
      duration: durationAsFloat,
    }));
  };

  const handleAssign = () => {
    const finalPayload = {
      ...quizData,
      template: parseInt(quizData.template),
      reattempt: parseInt(quizData.reattempt),
      show_answers:
        quizData.show_answers === "true" || quizData.show_answers === true,
      question_randomized:
        quizData.question_randomized === "true" ||
        quizData.question_randomized === true,
      section_id: quizData.section_id,
    };
    if (isEditing) {
      // call updateQuiz thunk (expects formData object and type param in your implementation)
      dispatch(updateQuiz(finalPayload, "class")).then(() =>
        history.push(`/course/view/${courseID}`)
      );
    } else {
      // create flow (unchanged)
      dispatch(createQuiz(finalPayload)).then(() =>
        history.push(`/course/view/${courseID}`)
      );
    }
  };

  const handleGoBack = () => {
    setOpenDiscardModal(true);
  };

  const handleModalConfirm = () => {
    history.push(`/template/questions/${courseID}`);
  };

  const handleCloseDiscardModal = () => setOpenDiscardModal(false);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleGoBack}>
          <ArrowLeftIcon />
          <span>{t("courseMonetisation.goBackBtn")}</span>
        </button>

        <div className={styles.quizIcon}>
          <img
            width={"100%"}
            height={"100%"}
            src={"/Image/quiz/addAttachment.svg"}
            alt="add attachment"
          />
        </div>
        <h1 className={styles.title}>{t("quiz.quiz")}</h1>
        <button className={styles.assignButton} onClick={handleAssign}>
          {t("assignment.assign")}
        </button>
      </div>

      <div className={styles.content}>
        {/* Left Panel */}
        <div className={styles.leftPanel}>
          <div className={styles.formGroup}>
            <div className={styles.labelContainer}>
              <div className={styles.redIcon}>
                <IntegrationInstructionsIcon sx={{ color: "#F33070" }} />
              </div>
              <label className={styles.label}>
                {t("quiz.quizTitle")}
                <span className={styles.required}>*</span>
              </label>
            </div>
            <input
              type="text"
              name="title"
              className={styles.input}
              placeholder="Add Quiz Title"
              value={quizData.title}
              onChange={handleInputChange}
            />
          </div>

          <div className={styles.formGroup}>
            <div className={styles.labelContainer}>
              <div className={styles.redIcon}>
                <AddToPhotosIcon sx={{ color: "#F33070" }} />
              </div>
              <label className={styles.label}>
                {t("quiz.addQuizTemplate")}
                <span className={styles.required}>*</span>
              </label>
              <ReusableTooltip title="Pick a quiz template" placement="top">
                <InfoIcon sx={{ width: "14px", height: "14px" }} />
              </ReusableTooltip>
            </div>
            <div className={styles.selectWrapper}>
              <CustomSelect
                name="template"
                value={String(quizData.template ?? "")} // ensure string for comparison
                onChange={handleSelectChange}
                options={[
                  // placeholder option represented as an option-like object but not selectable
                  { value: "", label: "Select Template" },
                  // actual template options
                  ...(templates?.map((template) => ({
                    value: String(template.id),
                    label: template.title,
                  })) || []),
                ]}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.labelContainer}>
              <div className={styles.redIcon}>
                <SubjectIcon sx={{ color: "#F33070" }} />
              </div>
              <label className={styles.label}>{t("quiz.instruction")}</label>
            </div>
            <textarea
              name="details"
              className={styles.textarea}
              placeholder="Share some instructions (optional)"
              value={quizData.details}
              onChange={handleInputChange}
            />
          </div>
        </div>

        {/* Right Panel */}
        <div className={styles.rightPanel}>
          <div className={styles.settingsHeader}>
            <div className={styles.settingsIcon}>
              <SettingsIcon sx={{ color: "#F33070" }} />
            </div>
            <h2 className={styles.settingsTitle}>{t("quiz.settings")}</h2>
          </div>

          <div className={styles.formGroup}>
            <div className={styles.timeSettingHeader}>
              <label className={styles.settingsLabel}>
                {t("quiz.timetoCompleteQuiz")}:
              </label>
              <ReusableIosSwitch
                checked={timeEnabled}
                onChange={handleTimeToggle}
              />
            </div>
            <div className={styles.timeInputs}>
              {["hours", "minutes", "seconds"].map((field) => (
                <div key={field} className={styles.timeInputGroup}>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    max={field === "hours" ? 99 : 59}
                    disabled={!timeEnabled}
                    className={styles.timeInput}
                    value={timeLimit[field]}
                    onChange={(e) => handleTimeChange(field, e.target.value)}
                  />
                  <span className={styles.timeLabel}>
                    {field.charAt(0).toUpperCase() + field.slice(1)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.settingsLabel}>Order of Questions:</label>
            <div className={styles.selectWrapper}>
              <CustomSelect
                name="question_randomized"
                value={quizData.question_randomized}
                options={[
                  { value: "false", label: "Do not reshuffle questions" },
                  { value: "true", label: "Reshuffle questions" },
                ]}
                onChange={handleSelectChange}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.settingsLabel}>
              {t("quiz.allowedAttempts")}:
            </label>
            <div className={styles.selectWrapper}>
              <CustomSelect
                name="reattempt"
                value={quizData.reattempt}
                options={Array.from({ length: 10 }, (_, i) => ({
                  value: String(i + 1),
                  label: String(i + 1),
                }))}
                onChange={handleSelectChange}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.settingsLabel}>
              {t("quiz.revealAnswers")}:
            </label>
            <div className={styles.selectWrapper}>
              <CustomSelect
                name="show_answers"
                value={quizData.show_answers}
                options={[
                  { value: "false", label: "Do not reveal answers" },
                  { value: "true", label: "Immediately reveal after the quiz" },
                ]}
                onChange={handleSelectChange}
              />
            </div>
          </div>
        </div>
      </div>

      {openDiscardModal && (
        <DiscardModal
          onDiscard={handleModalConfirm}
          onClose={handleCloseDiscardModal}
        />
      )}
    </div>
  );
};

export default QuizCreator;
